import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { PhoneLink, EmailLink, sortByUpcoming, sortCurrentThenFuture, bookingBucket, arrivalChip, durationBucketKey, formatDuration, fmtDate } from "../lib/formatters";
import { Upload, Trash2, Plus, X, Copy, Send, Pencil, Check, MessageCircle, PlusCircle, Mail } from "lucide-react";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import UploadPreviewModal from "../components/UploadPreviewModal";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import DestCityFilter from "../components/DestCityFilter";
import { useCollapsibleGroups } from "../lib/useCollapsibleGroups";
import PlacementAnalyticsCard from "../components/program/PlacementAnalyticsCard";
import BulkPlacementModal from "../components/program/BulkPlacementModal";
import BulkRoomAssignModal from "../components/BulkRoomAssignModal";
import BulkActionBar from "../components/BulkActionBar";
import NewProjectModal from "../components/program/NewProjectModal";

const CS = { USD: "$", GBP: "£", EUR: "€", INR: "₹" };
const GST_LABEL = {
  nil: "Nil", inclusive: "Incl. 18%", "5": "5%", "10": "10%", "15": "15%", "18": "18%",
};

export default function ProgramBooking() {
  const { user } = useAuth();
  const role = user?.real_role || user?.effective_role;
  const isFounder = role === "FOUNDER";
  // GEHM has view-only access — they can see every participant landing at
  // their guest house, but cannot add, upload, delete, or reassign placements.
  const canEdit = role !== "GEHM";
  // Founder-only delete rule applies across every core collection (program,
  // weekend, arrivals, C-form, events, activities, service requests, etc.)
  const canDelete = role === "FOUNDER";
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [specialisations, setSpecialisations] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({});
  const [tab, setTab] = useState("active"); // active | past
  const [placementEdits, setPlacementEdits] = useState({}); // id -> pending project value (renamed to Project column)
  const [placementNameEdits, setPlacementNameEdits] = useState({}); // id -> pending Placement value
  const [filter, setFilter] = useState({ destination: "", city: "" });
  const [selected, setSelected] = useState(() => new Set()); // ids selected for bulk placement
  const [bulkOpen, setBulkOpen] = useState(false);
  const [roomBulkOpen, setRoomBulkOpen] = useState(false);
  const [bulkVal, setBulkVal] = useState("");
  const [newProj, setNewProj] = useState(null); // { forRow, name, destination, city }
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem("aii-groupby:program-bookings") || "month"); // "month" | "city" | "bucket" | "none"
  const [projectFilter, setProjectFilter] = useState(""); // "" = all projects
  const [thisWeekOnly, setThisWeekOnly] = useState(false); // one-tap "arriving in next 7 days" filter
  const { collapsed: collapsedMonths, toggle: toggleMonth } = useCollapsibleGroups("program-bookings");
  const fileRef = useRef();

  const load = async () => {
    const q = new URLSearchParams({ archived: String(tab === "past") });
    if (filter.destination) q.set("destination", filter.destination);
    if (filter.city) q.set("city", filter.city);
    const [p, pr, pl, sp] = await Promise.all([
      api.get(`/program-bookings?${q.toString()}`),
      api.get("/projects").catch(()=>({data:[]})),
      api.get("/placements").catch(()=>({data:[]})),
      api.get("/specialisations").catch(()=>({data:[]})),
    ]);
    // Active tab uses the Current-then-Future sort so participants already
    // on the ground bubble to the top. Past tab keeps the newest-first
    // legacy behaviour so it reads like a history log.
    setRows(tab === "past" ? sortByUpcoming(p.data, "start_date") : sortCurrentThenFuture(p.data));
    setProjects(pr.data);
    setPlacements(pl.data);
    setSpecialisations(sp.data);
    setPlacementEdits({});
    setPlacementNameEdits({});
  };
  useEffect(() => { load(); }, [tab, filter.destination, filter.city]);

  const add = async () => {
    if (!form.customer || !form.programme) return toast.error("Customer & programme required");
    await api.post("/program-bookings", form);
    setForm({}); setAdding(false); await load(); toast.success("Added");
  };
  const del = async (id) => { await api.delete(`/program-bookings/${id}`); await load(); };
  const [delTarget, setDelTarget] = useState(null);
  // Piggy-back on the existing `selected` Set (used by the placement
  // Bulk-Assign flow) so users only ever have ONE selection state per
  // page. bulkDelete simply fans the existing single-delete endpoint
  // out across every selected id — Founder-only guard already enforced
  // server-side.
  const bulkDelete = async () => {
    const ids = [...selected].filter(Boolean);
    await Promise.all(ids.map(id => api.delete(`/program-bookings/${id}`)));
    setSelected(new Set());
    await load();
  };

  const provisionCreds = async (booking) => {
    // Idempotent: ensures a participant user exists, then regenerates a fresh password.
    await api.post(`/participants/${booking.id}/create-credentials`);
    const { data } = await api.post(`/participants/${booking.id}/send-credentials`, { send_email: false });
    return data; // {email, password, app_url, whatsapp_link}
  };

  const copyCreds = async (booking) => {
    try {
      const data = await provisionCreds(booking);
      const msg = `Aii Group Participant Portal\n\nApp: ${data.app_url}\nLogin: ${data.email}\nPassword: ${data.password}`;
      await navigator.clipboard.writeText(msg);
      toast.success("Login credentials copied to clipboard");
    } catch (e) { toast.error(e?.response?.data?.detail || "Copy failed"); }
  };

  const sendCreds = async (booking) => {
    // Guard: WhatsApp is the primary channel — abort with a helpful hint if
    // the booking has no phone/whatsapp on file.
    const hasPhone = ((booking.whatsapp || "").trim() || (booking.telephone || booking.phone || "").trim());
    if (!hasPhone) {
      toast.error("Add a WhatsApp / phone number on this booking first");
      return;
    }
    try {
      // Ensure user exists, then rotate password. `send_email: false` because
      // WhatsApp is the primary delivery channel per Founder preference —
      // the participant will see the pre-filled message and hit Send.
      await api.post(`/participants/${booking.id}/create-credentials`);
      const { data } = await api.post(`/participants/${booking.id}/send-credentials`, { send_email: false });
      if (!data.whatsapp_link) {
        toast.error("Couldn't build WhatsApp link — phone number invalid");
        return;
      }
      // Copy to clipboard as a safety net, then open WhatsApp with the
      // credentials pre-filled so the Founder just taps Send.
      const msg = `App: ${data.app_url}\nLogin: ${data.email}\nPassword: ${data.password}`;
      try { await navigator.clipboard.writeText(msg); }
      catch (err) { console.debug("clipboard blocked:", err?.message); }
      window.open(data.whatsapp_link, "_blank");
      // Optimistically mark the row as "sent" so the founder immediately sees
      // the timestamp without a full reload. The backend has already denormalized
      // `login_last_sent_at` onto the booking, so the next load will confirm it.
      setRows(prev => prev.map(x => x.id === booking.id
        ? { ...x, login_last_sent_at: data.login_last_sent_at }
        : x));
      toast.success("WhatsApp opened — hit Send to deliver the login");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Email counterpart — when the participant has no WhatsApp (or the
  // Founder prefers email delivery), fire the same send-credentials call
  // with send_email:true so the backend routes the credentials through the
  // Resend email template.  Guard on a real (non-@participants.aiigroup.com)
  // email so we don't email into the void.
  const sendCredsEmail = async (booking) => {
    const email = (booking.email || "").trim();
    if (!email || email.toLowerCase().endsWith("@participants.aiigroup.com")) {
      toast.error("Add a real personal email on this booking first — the placeholder participants.aiigroup.com address won't reach the candidate");
      return;
    }
    try {
      await api.post(`/participants/${booking.id}/create-credentials`);
      const { data } = await api.post(`/participants/${booking.id}/send-credentials`, { send_email: true });
      // Belt & braces: also copy to clipboard so the Founder can paste into
      // any other channel if the email deliverability lags.
      const msg = `App: ${data.app_url}\nLogin: ${data.email}\nPassword: ${data.password}`;
      try { await navigator.clipboard.writeText(msg); }
      catch (err) { console.debug("clipboard blocked:", err?.message); }
      setRows(prev => prev.map(x => x.id === booking.id
        ? { ...x, login_last_sent_at: data.login_last_sent_at }
        : x));
      toast.success(`Email sent to ${email} — credentials copied to clipboard as backup`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
  };
  const [pendingUpload, setPendingUpload] = useState(null);
  const upload = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setPendingUpload(f);
    if (fileRef.current) fileRef.current.value = "";
  };
  const startEditPlacement = (r) => setPlacementEdits((p) => ({ ...p, [r.id]: r.project_placement || "" }));
  const cancelEditPlacement = (id) => setPlacementEdits((p) => { const n = { ...p }; delete n[id]; return n; });
  const savePlacement = async (id) => {
    const val = placementEdits[id] || "";
    await api.patch(`/program-bookings/${id}`, { project_placement: val });
    toast.success(val ? "Placement saved" : "Placement cleared");
    await load();
  };

  // ---- Placement column (separate from Project) --------------------------
  // PCWM types a placement name; if the value isn't in the /placements
  // registry, we auto-POST it so it appears in every future dropdown.
  // Edit / delete of registry entries lives in the Registries page.
  const startEditPlacementName = (r) => setPlacementNameEdits((p) => ({ ...p, [r.id]: r.placement_name || "" }));
  const cancelEditPlacementName = (id) => setPlacementNameEdits((p) => { const n = { ...p }; delete n[id]; return n; });
  const savePlacementName = async (id) => {
    const val = (placementNameEdits[id] || "").trim();
    // Auto-register new placement names so PCWM doesn't juggle two screens.
    if (val && !placements.some((p) => (p.name || "").toLowerCase() === val.toLowerCase())) {
      try { await api.post("/placements", { name: val }); }
      catch (e) { console.debug("placement autoregister failed:", e?.message); }
    }
    await api.patch(`/program-bookings/${id}`, { placement_name: val });
    toast.success(val ? `Placement "${val}" saved` : "Placement cleared");
    await load();
  };
  const deletePlacementFromRegistry = async (placementId, placementName) => {
    if (!window.confirm(`Remove "${placementName}" from the placement dropdown?\n\nExisting bookings that already reference this placement will keep the text — this only removes it from the reusable list.`)) return;
    try {
      await api.delete(`/placements/${placementId}`);
      toast.success(`"${placementName}" removed from the list`);
      const pl = await api.get("/placements").catch(() => ({ data: [] }));
      setPlacements(pl.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };
  const addNewProject = async (r) => {
    // Open a proper inline dialog pre-filled with the booking's destination
    // + city so PCWM doesn't need to retype them.  She can still edit either
    // field before saving — useful when she's creating a placement in a
    // nearby city.
    setNewProj({
      forRow: r.id,
      name: "",
      destination: r.destination || "",
      city: r.city || "",
    });
  };

  const saveNewProject = async () => {
    if (!newProj) return;
    const name = (newProj.name || "").trim();
    if (!name) return toast.error("Enter a placement / project name");
    const existing = projects.find((p) => (p.name || "").toLowerCase() === name.toLowerCase());
    if (!existing) {
      try {
        await api.post("/projects", {
          name,
          destination: newProj.destination || "",
          city: newProj.city || "",
          extra: JSON.stringify({ destination: newProj.destination || "", city: newProj.city || "" }),
        });
        toast.success(`"${name}" added to placement list`);
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not add project"); return; }
    }
    const pr = await api.get("/projects").catch(() => ({ data: [] }));
    setProjects(pr.data);
    setPlacementEdits((p) => ({ ...p, [newProj.forRow]: name }));
    setBulkVal(name);
    setNewProj(null);
  };

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAllVisible = () => {
    setSelected(prev => {
      if (rows.length > 0 && rows.every(r => prev.has(r.id))) return new Set();
      return new Set(rows.map(r => r.id));
    });
  };
  const clearSelected = () => setSelected(new Set());

  const applyBulkPlacement = async () => {
    const val = bulkVal || "";
    if (!val) return toast.error("Pick a placement to assign");
    if (selected.size === 0) return toast.error("Select at least one booking");
    // Snapshot the prior placement for every selected row so the Undo action
    // can restore each row to exactly what it had before the bulk assign.
    const ids = [...selected];
    const priorById = {};
    for (const r of rows) {
      if (selected.has(r.id)) priorById[r.id] = r.project_placement || "";
    }
    try {
      await Promise.all(ids.map(id => api.patch(`/program-bookings/${id}`, { project_placement: val })));
      const count = ids.length;
      // Show a Sonner action toast — 5 seconds gives PCWM time to spot a
      // mis-click and revert without hunting through the table.
      toast.success(`Placement "${val}" assigned to ${count} booking${count === 1 ? "" : "s"}`, {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await Promise.all(ids.map(id => api.patch(`/program-bookings/${id}`, { project_placement: priorById[id] || "" })));
              toast.info(`Reverted ${count} placement${count === 1 ? "" : "s"}`);
              await load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Undo failed"); }
          },
        },
      });
      setBulkOpen(false); setBulkVal(""); clearSelected(); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk assign failed"); }
  };

  // ---- Placement analytics --------------------------------------------
  // Client-side aggregate: group current-month bookings (by start_date) by
  // their assigned placement and surface the top 5 as a compact tile.  PCWM
  // sees at a glance which placements are hosting the most participants
  // right now and can rebalance if any single site is over-committed.
  const placementSummary = (() => {
    const today = new Date();
    const ym = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    const totals = {};
    let monthCount = 0;
    let unassigned = 0;
    for (const r of rows) {
      const start = (r.start_date || "").slice(0, 7);
      if (start !== ym) continue;
      monthCount += 1;
      const name = (r.project_placement || "").trim();
      if (!name) { unassigned += 1; continue; }
      totals[name] = (totals[name] || 0) + 1;
    }
    const top = Object.entries(totals).sort((a, b) => b[1] - a[1]).slice(0, 5);
    return { top, monthCount, unassigned, ym };
  })();

  const FIELDS = [
    { k:"customer", label:"Customer" }, { k:"programme", label:"Programme" }, { k:"duration", label:"Duration" },
    { k:"start_date", label:"Start date", t:"date" }, { k:"end_date", label:"End date", t:"date" }, { k:"dob", label:"Date of birth", t:"date" },
    { k:"email", label:"Email", t:"email" }, { k:"telephone", label:"Telephone" },
    { k:"gender", label:"Gender", options: ["Male","Female","Other","Prefer not to say"] },
    { k:"travelling_with", label:"Travelling with" }, { k:"dietary_requirements", label:"Dietary requirements" },
    { k:"medical_conditions", label:"Medical conditions" }, { k:"special_requirements", label:"Special requirements" },
    { k:"project_placement", label:"Project placement", options: projects.map(p=>p.name) },
    { k:"specialisation", label:"Specialisation", options: specialisations.map(s=>s.name) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold">Program Bookings</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <DestCityFilter value={filter} onChange={setFilter} />
          {(() => {
            // Project filter chips — derived from the unique catalog_program_name
            // values present in the current list.  Clicking a chip zooms into that
            // project; clicking All clears.  Empty when no projects surface yet.
            const projs = [...new Set(rows.map(r => (r.catalog_program_name || r.program_name || "").trim()).filter(Boolean))].sort();
            if (projs.length < 2) return null;
            return (
              <div className="flex items-center gap-1 flex-wrap" data-testid="pb-project-filter">
                <button data-testid="pb-project-all" onClick={() => setProjectFilter("")}
                        className={`text-xs px-2 py-1 rounded-full font-semibold ${!projectFilter ? "bg-brand text-white" : "bg-white border border-[color:var(--aii-border)] hover:bg-slate-50"}`}>
                  All projects
                </button>
                {projs.slice(0, 8).map(p => (
                  <button key={`pf-${p}`} data-testid={`pb-project-chip-${p}`}
                          onClick={() => setProjectFilter(projectFilter === p ? "" : p)}
                          className={`text-xs px-2 py-1 rounded-full font-semibold truncate max-w-[180px] ${projectFilter === p ? "bg-brand text-white" : "bg-white border border-[color:var(--aii-border)] hover:bg-slate-50"}`}
                          title={p}>
                    {p}
                  </button>
                ))}
              </div>
            );
          })()}
          <div className="inline-flex rounded-md border border-[color:var(--aii-border)] overflow-hidden" data-testid="pb-groupby">
            {["month","city","bucket","none"].map(g => (
              <button key={`gb-${g}`} data-testid={`pb-groupby-${g}`}
                      onClick={() => { setGroupBy(g); try { localStorage.setItem("aii-groupby:program-bookings", g); } catch { /* ignore */ } }}
                      title={g === "none" ? "Show a flat list without groups" : g === "bucket" ? "Group by urgency: Currently on Ground / Landing This Week / Landing This Month" : `Group by ${g}`}
                      className={`px-2.5 py-1.5 text-xs font-semibold ${groupBy === g ? "bg-brand text-white" : "bg-white text-[color:var(--aii-text-2)] hover:bg-slate-50"}`}>
                {g === "month" ? "Month" : g === "city" ? "City" : g === "bucket" ? "Bucket" : "None"}
              </button>
            ))}
          </div>
          <button
            data-testid="pb-this-week"
            onClick={() => setThisWeekOnly(v => !v)}
            title="Show only participants arriving in the next 7 days — perfect for the weekly team huddle"
            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
              thisWeekOnly
                ? "bg-brand text-white border-brand"
                : "bg-white text-[color:var(--aii-text-2)] border-[color:var(--aii-border)] hover:bg-slate-50"
            }`}>
            {thisWeekOnly ? "✓ " : ""}This week
          </button>
          <div className="inline-flex rounded-md border border-[color:var(--aii-border)] overflow-hidden">
            <button data-testid="pb-tab-active" onClick={()=>setTab("active")} className={`px-3 py-1.5 text-sm font-semibold ${tab==="active"?"bg-brand text-white":"bg-white"}`}>Active</button>
            <button data-testid="pb-tab-past" onClick={()=>setTab("past")} className={`px-3 py-1.5 text-sm font-semibold ${tab==="past"?"bg-brand text-white":"bg-white"}`}>Past Participants</button>
          </div>
          {canEdit && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={upload} className="hidden" data-testid="pb-upload-input"/>
              <button data-testid="pb-upload" onClick={() => fileRef.current?.click()} className="text-sm px-3 py-2 border border-[color:var(--aii-border)] bg-white rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5"><Upload size={14}/> Upload</button>
              <button data-testid="pb-add-toggle" onClick={()=>setAdding(true)} className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5"><Plus size={14}/> Add row</button>
            </>
          )}
        </div>
      </div>

      <PlacementAnalyticsCard summary={placementSummary}/>

      {adding && (
        <div className="aii-card p-5">
          <div className="flex justify-between items-center mb-3"><h3 className="font-semibold">New booking</h3><button onClick={()=>setAdding(false)}><X size={16}/></button></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {FIELDS.map(f => (
              <div key={f.k}>
                <label className="text-xs font-semibold text-[color:var(--aii-text-2)] uppercase tracking-wider">{f.label}</label>
                {f.options ? (
                  <select data-testid={`pb-${f.k}`} value={form[f.k]||""} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))}
                    className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                    <option value="">Select…</option>
                    {f.options.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input data-testid={`pb-${f.k}`} type={f.t||"text"} value={form[f.k]||""} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))}
                    className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-4"><button data-testid="pb-save" onClick={add} className="bg-brand text-white font-semibold px-5 py-2 rounded-md hover:bg-brand-dark">Save</button></div>
        </div>
      )}

      <div className="aii-card overflow-x-auto">
        {(() => {
          const cities = [...new Set(rows.map(r => (r.city || "").trim()).filter(Boolean))].sort();
          if (cities.length === 0) return null;
          return (
            <div className="p-3 border-b border-[color:var(--aii-border)] flex items-center gap-2 flex-wrap" data-testid="pb-city-chips">
              <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">City</span>
              <button data-testid="pb-city-all"
                      onClick={() => setFilter(f => ({ ...f, city: "" }))}
                      className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${!filter.city ? "bg-brand text-white border-brand" : "bg-white border-[color:var(--aii-border)] text-[color:var(--aii-text-2)] hover:bg-slate-50"}`}>
                All ({rows.length})
              </button>
              {cities.map(c => {
                const n = rows.filter(r => (r.city || "").trim() === c).length;
                const active = filter.city === c;
                return (
                  <button key={`city-${c}`} data-testid={`pb-city-${c}`}
                          onClick={() => setFilter(f => ({ ...f, city: active ? "" : c }))}
                          className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${active ? "bg-brand text-white border-brand" : "bg-white border-[color:var(--aii-border)] text-[color:var(--aii-text-2)] hover:bg-slate-50"}`}>
                    {c} ({n})
                  </button>
                );
              })}
            </div>
          );
        })()}
        <div className="overflow-x-auto">
          {canDelete && (
            <BulkActionBar count={selected.size} label="program booking"
              onClear={() => setSelected(new Set())}
              onDelete={bulkDelete}/>
          )}
          <table className="aii-table">
            <thead><tr>
              <th className="w-8">
                {canEdit && (
                  <input type="checkbox" data-testid="pb-select-all"
                         checked={rows.length > 0 && rows.every(r => selected.has(r.id))}
                         onChange={selectAllVisible}
                         title="Select all visible rows for bulk placement assign"/>
                )}
              </th>
              <th>Partner</th><th>Destination</th><th>City</th><th>Participant</th><th>Gender</th><th>DOB</th>
              <th>Programme</th><th>Project</th><th>Duration</th><th>Start</th><th>End</th>
              <th>Meal / Diet</th><th>Medical</th><th>Buddies</th>
              {isFounder && <th>Price</th>}
              <th>Placement</th><th></th>
            </tr></thead>
            <tbody>
              {rows.length===0 && <tr><td colSpan={isFounder ? 18 : 17} className="text-center py-10 text-[color:var(--aii-muted)]">No bookings.</td></tr>}
              {(() => {
                // Dynamic grouping: user picks City / Month / None from the
                // segmented control.  We always run through the same header +
                // row-render pipeline so the collapse hook stays consistent.
                // Project chip filter applies before grouping so counts match.
                const filteredRows = (projectFilter
                  ? rows.filter(r => ((r.catalog_program_name || r.program_name || "").trim() === projectFilter))
                  : rows).filter(r => {
                    // "This week" pill — arrival within next 7 days (inclusive of today).
                    if (!thisWeekOnly) return true;
                    if (!r.start_date) return false;
                    const today = new Date(); today.setHours(0,0,0,0);
                    const start = new Date(r.start_date.slice(0,10) + "T00:00:00");
                    const days = Math.round((start - today) / 86400000);
                    return days >= 0 && days <= 7;
                  });
                const keyFor = (r) => {
                  if (groupBy === "city") return (r.city || "").trim() || "No city";
                  if (groupBy === "month") return (r.start_date || "").slice(0, 7) || "No start date";
                  if (groupBy === "bucket") return durationBucketKey(r);
                  return "__all__";
                };
                const cols = isFounder ? 18 : 17;
                const groups = new Map();
                for (const r of filteredRows) {
                  const k = keyFor(r);
                  if (!groups.has(k)) groups.set(k, []);
                  groups.get(k).push(r);
                }
                const out = [];
                // Custom ordering for the "Bucket" grouping — alphabetical
                // sort would place "Currently on Ground" between L and P, so
                // we anchor the founder's heart-rate view to urgency order.
                const BUCKET_ORDER = ["Currently on Ground", "Landing This Week", "Landing This Month", "Landing Later", "No start date", "Past"];
                const orderedGroups = groupBy === "bucket"
                  ? [...groups.entries()].sort((a, b) => BUCKET_ORDER.indexOf(a[0]) - BUCKET_ORDER.indexOf(b[0]))
                  : [...groups.entries()].sort();
                for (const [gk, list] of orderedGroups) {
                  const isCollapsed = groupBy !== "none" && collapsedMonths.has(gk);
                  if (groupBy !== "none") {
                    out.push(
                      <tr key={`hdr-${gk}`} data-testid={`pb-month-hdr-${gk}`} className="bg-[color:var(--aii-green-tint)]/50 cursor-pointer hover:bg-[color:var(--aii-green-tint)]"
                          onClick={() => toggleMonth(gk)}>
                        <td colSpan={cols} className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold text-brand select-none">
                          {isCollapsed ? "▸" : "▾"} {gk} · {list.length} booking{list.length === 1 ? "" : "s"}
                        </td>
                      </tr>
                    );
                  }
                  if (isCollapsed) continue;
                  // Show a tiny "Currently on the ground" / "Arriving soon"
                  // divider in the Active tab with no grouping — makes the
                  // current-vs-future split visually obvious. Skipped when
                  // the user has grouped by city/month (grouping already
                  // provides its own visual break).
                  let lastBucket = null;
                  const showBucketDividers = tab === "active" && groupBy === "none";
                  for (const r of list) {
                    if (showBucketDividers) {
                      const b = bookingBucket(r);
                      if (b !== lastBucket && b !== "past") {
                        lastBucket = b;
                        out.push(
                          <tr key={`bucket-${gk}-${b}`} data-testid={`pb-bucket-${b}`}
                              className={b === "current"
                                ? "bg-emerald-50/70"
                                : "bg-amber-50/50"}>
                            <td colSpan={cols} className={`px-3 py-1 text-[11px] uppercase tracking-widest font-bold select-none ${
                              b === "current" ? "text-emerald-700" : "text-amber-700"
                            }`}>
                              {b === "current"
                                ? "● Currently on the ground"
                                : "○ Arriving soon"}
                              <span className="text-[color:var(--aii-muted)] ml-2 font-normal normal-case tracking-normal">
                                {list.filter(x => bookingBucket(x) === b).length}
                                {" "}participant{list.filter(x => bookingBucket(x) === b).length === 1 ? "" : "s"}
                              </span>
                            </td>
                          </tr>
                        );
                      }
                    }
                    out.push(
                <tr key={r.id} className={selected.has(r.id) ? "bg-[color:var(--aii-green-tint)]/40" : ""}>
                  <td>
                    {canEdit && (
                      <input type="checkbox" data-testid={`pb-select-${r.id}`}
                             checked={selected.has(r.id)}
                             onChange={() => toggleSelect(r.id)}/>
                    )}
                  </td>
                  <td className="font-medium text-brand" data-testid={`pb-partner-${r.id}`}>{r.partner_name || <span className="italic text-[color:var(--aii-muted)] font-normal">—</span>}</td>
                  <td className="text-xs whitespace-nowrap">{r.destination || "—"}</td>
                  <td className="text-xs whitespace-nowrap">{r.city || "—"}</td>
                  <td className="font-medium">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span data-testid={`pb-customer-${r.id}`}>{r.customer}</span>
                        {(() => {
                          const chip = arrivalChip(r);
                          if (!chip) return null;
                          return (
                            <span
                              data-testid={`pb-chip-${r.id}`}
                              title={`Arrival ${r.start_date || "?"} · Departure ${r.end_date || "?"}`}
                              className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border rounded font-bold whitespace-nowrap ${chip.cls}`}>
                              {chip.label}
                            </span>
                          );
                        })()}
                      </div>
                      {/* Compact contact strip: email → mailto, phone → wa.me.
                          Only real (non-placeholder) emails render as links so
                          we don't dead-end into participants.aiigroup.com. */}
                      {((r.email && !r.email.toLowerCase().endsWith("@participants.aiigroup.com")) || r.telephone || r.whatsapp) && (
                        <div className="flex items-center gap-2 flex-wrap text-[11px] font-normal">
                          {r.email && !r.email.toLowerCase().endsWith("@participants.aiigroup.com") && (
                            <span data-testid={`pb-email-${r.id}`} className="inline-flex items-center gap-1">
                              <Mail size={10} className="text-[color:var(--aii-muted)]"/>
                              <EmailLink value={r.email}/>
                            </span>
                          )}
                          {(r.whatsapp || r.telephone) && (
                            <span data-testid={`pb-phone-${r.id}`} className="inline-flex items-center gap-1">
                              <MessageCircle size={10} className="text-[#25D366]"/>
                              <PhoneLink value={r.whatsapp || r.telephone}/>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{r.gender||"—"}</td>
                  <td className="whitespace-nowrap">{fmtDate(r.dob)||"—"}</td>
                  <td>{r.programme}</td>
                  <td data-testid={`pb-project-${r.id}`} className="text-xs">
                    {(() => {
                      // Show the resolved project value as a "Project placement"
                      // stamp card once it's set — same visual language as the
                      // Arrivals "Pickup by NAME · locked" card so Founder sees
                      // consistent read-only chips across the app. Empty rows
                      // show a muted em-dash placeholder.
                      const val = r.catalog_program_name || r.program_name || r.project_placement || "";
                      if (!val) return <span className="italic text-[color:var(--aii-muted)]">—</span>;
                      return (
                        <div className="bg-slate-50 border border-[color:var(--aii-border)] rounded p-2 min-w-[160px]">
                          <div className="text-[9px] uppercase tracking-widest text-brand font-bold flex items-center gap-1">
                            <PlusCircle size={9}/> Project placement
                          </div>
                          <div className="font-semibold text-xs mt-0.5 text-[color:var(--aii-text-1)]" data-testid={`pb-project-name-${r.id}`}>
                            {val}
                          </div>
                          <div className="text-[10px] text-[color:var(--aii-muted)] uppercase tracking-widest mt-1">
                            Assigned
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td>{formatDuration(r.duration)||"—"}</td>
                  <td className="whitespace-nowrap">{fmtDate(r.start_date)||"—"}</td>
                  <td className="whitespace-nowrap">{fmtDate(r.end_date)||"—"}</td>
                  <td className="text-xs max-w-[140px] truncate" title={r.meal_requests}>{r.meal_requests || "—"}</td>
                  <td className={`text-xs max-w-[140px] truncate ${r.medical_conditions ? "text-rose-700 font-semibold" : ""}`} title={r.medical_conditions}>{r.medical_conditions || "—"}</td>
                  <td className="text-xs max-w-[120px] truncate" title={r.buddies}>{r.buddies || "—"}</td>
                  {isFounder && (
                    <td className="whitespace-nowrap font-semibold text-brand" data-testid={`pb-price-${r.id}`}>
                      {r.sale_price ? `${CS[r.sale_currency] || r.sale_currency}${Number(r.sale_price).toLocaleString()}` : "—"}
                      {r.gst_slab && r.gst_slab !== "nil" && <span className="text-[10px] text-[color:var(--aii-muted)] font-normal ml-1">{GST_LABEL[r.gst_slab] || ""}</span>}
                    </td>
                  )}
                  <td data-testid={`pb-placementname-cell-${r.id}`}>
                    {(() => {
                      // Reusable "Assigned" stamp — mirrors the Project column
                      // and the Arrivals "Pickup by NAME · locked" card. Kept
                      // as an inline IIFE so we don't have to plumb another
                      // component + import through this already-dense table.
                      const stamp = r.placement_name ? (
                        <div className="bg-slate-50 border border-[color:var(--aii-border)] rounded p-2 min-w-[160px]">
                          <div className="text-[9px] uppercase tracking-widest text-brand font-bold flex items-center gap-1">
                            <Check size={9}/> Placement
                          </div>
                          <div className="font-semibold text-xs mt-0.5 text-[color:var(--aii-text-1)]" data-testid={`pb-placementname-stamp-${r.id}`}>
                            {r.placement_name}
                          </div>
                          <div className="text-[10px] text-[color:var(--aii-muted)] uppercase tracking-widest mt-1">
                            Assigned
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-[color:var(--aii-muted)] italic">Not yet assigned</span>
                      );
                      if (!canEdit) return stamp;
                      if (placementNameEdits[r.id] !== undefined) {
                        return (
                          <div className="flex items-center gap-1">
                            <input list={`pb-placement-list-${r.id}`}
                                   value={placementNameEdits[r.id]}
                                   onChange={e => setPlacementNameEdits(p => ({ ...p, [r.id]: e.target.value }))}
                                   placeholder="Type or pick…"
                                   className="px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-xs min-w-[160px]"
                                   data-testid={`pb-placementname-edit-${r.id}`}/>
                            <datalist id={`pb-placement-list-${r.id}`}>
                              {(() => {
                                // Scope the dropdown to placements matching this booking's
                                // destination + city — Nepal bookings never see Jaipur
                                // placements.  Placements with no destination stay global.
                                const rd = (r.destination || "").toLowerCase();
                                const rc = (r.city || "").toLowerCase();
                                const scoped = placements.filter(p => {
                                  const pd = (p.destination || "").toLowerCase();
                                  const pc = (p.city || "").toLowerCase();
                                  return !pd || (pd === rd && (!rc || !pc || pc === rc));
                                });
                                return scoped.map(p => <option key={p.id} value={p.name}/>);
                              })()}
                            </datalist>
                            <button onClick={() => savePlacementName(r.id)} data-testid={`pb-placementname-save-${r.id}`} title="Save — new names are auto-added to the list" className="text-emerald-700 p-1 hover:bg-emerald-50 rounded"><Check size={13}/></button>
                            <button onClick={() => cancelEditPlacementName(r.id)} title="Cancel" className="text-[color:var(--aii-muted)] p-1 hover:bg-slate-100 rounded"><X size={13}/></button>
                            {(() => {
                              const match = placements.find(p => (p.name || "").toLowerCase() === (r.placement_name || "").toLowerCase());
                              if (!match) return null;
                              return (
                                <button onClick={() => deletePlacementFromRegistry(match.id, match.name)}
                                        data-testid={`pb-placementname-registry-del-${r.id}`}
                                        title={`Remove "${match.name}" from the reusable list`}
                                        className="text-[color:var(--aii-muted)] hover:text-red-600 p-1 rounded"><Trash2 size={12}/></button>
                              );
                            })()}
                          </div>
                        );
                      }
                      // canEdit + not editing → show the stamp card next to
                      // an inline Pencil so Founder / BDOM / PCWM can jump
                      // into edit mode without hunting for a button.
                      return (
                        <div className="flex items-start gap-1">
                          <div className="flex-1">{stamp}</div>
                          <button onClick={() => startEditPlacementName(r)} data-testid={`pb-placementname-edit-btn-${r.id}`} title="Edit placement" className="text-[color:var(--aii-muted)] hover:text-brand p-1 shrink-0"><Pencil size={12}/></button>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="text-right">
                    {canEdit && (
                    <div className="flex items-center justify-end gap-1">
                      {isFounder && (
                        <>
                          {/* Sending login credentials via Copy /
                              WhatsApp / Email is now Founder-only.
                              Partners asked that no team member —
                              BDOM / GEHM / PCWM / SMCM — be able to
                              contact participants directly, so the
                              three send buttons are collapsed under
                              the Founder-only branch. */}
                          <button data-testid={`pb-copy-${r.id}`} onClick={()=>copyCreds(r)} title="Copy login credentials" className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] text-[color:var(--aii-text-1)] flex items-center gap-1"><Copy size={11}/> Copy</button>
                          <button data-testid={`pb-cred-${r.id}`} onClick={()=>sendCreds(r)}
                            title={r.login_last_sent_at ? `Last sent ${fmtDate(r.login_last_sent_at)} — tap to resend` : "Send login via WhatsApp to the participant's phone number"}
                            className={`text-xs px-2 py-1 border rounded flex items-center gap-1 ${
                              r.login_last_sent_at
                                ? "border-emerald-600 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                                : "border-brand text-brand hover:bg-[color:var(--aii-green-tint)]"
                            }`}>
                            {r.login_last_sent_at
                              ? <><Check size={11}/> Sent · <MessageCircle size={10}/> Resend</>
                              : <><Send size={11}/> Send login</>}
                          </button>
                          <button data-testid={`pb-email-${r.id}`} onClick={()=>sendCredsEmail(r)}
                            title="Send login by email — use when the candidate has no WhatsApp"
                            className="text-xs px-2 py-1 border border-sky-600 text-sky-700 bg-sky-50 hover:bg-sky-100 rounded flex items-center gap-1">
                            <Mail size={11}/> Email
                          </button>
                        </>
                      )}
                      {canDelete && <button data-testid={`pb-del-${r.id}`} onClick={()=>setDelTarget(r)} className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14}/></button>}
                    </div>
                    )}
                  </td>
                </tr>
                  );
                  }
                }
                return out;
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-brand text-white rounded-full shadow-2xl px-4 py-2.5 flex items-center gap-3 z-40" data-testid="pb-bulk-bar">
          <span className="text-sm font-semibold" data-testid="pb-bulk-count">{selected.size} selected</span>
          <span className="text-white/50">·</span>
          <button data-testid="pb-bulk-open" onClick={() => setBulkOpen(true)}
                  className="text-sm font-semibold px-3 py-1 rounded bg-white text-brand hover:bg-slate-50 flex items-center gap-1">
            Assign placement
          </button>
          <button data-testid="pb-bulk-room-open" onClick={() => setRoomBulkOpen(true)}
                  className="text-sm font-semibold px-3 py-1 rounded bg-white text-brand hover:bg-slate-50 flex items-center gap-1">
            Assign room
          </button>
          <button data-testid="pb-bulk-clear" onClick={clearSelected}
                  className="text-xs text-white/70 hover:text-white p-1"><X size={13}/></button>
        </div>
      )}

      <BulkRoomAssignModal
        open={roomBulkOpen}
        onClose={() => setRoomBulkOpen(false)}
        selectedIds={[...selected]}
        rows={rows}
        onDone={async () => { setRoomBulkOpen(false); clearSelected(); await load(); }}
      />

      <BulkPlacementModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        selectedSize={selected.size}
        bulkVal={bulkVal}
        setBulkVal={setBulkVal}
        projects={projects}
        onApply={applyBulkPlacement}
        onCreateNew={() => {
          const first = rows.find(x => selected.has(x.id));
          if (first) addNewProject(first);
        }}
      />

      <NewProjectModal
        newProj={newProj}
        setNewProj={setNewProj}
        onSave={saveNewProject}
      />
      <DeleteConfirmDialog
        open={!!delTarget}
        title="Delete this program booking?"
        description="The booking + its sale, arrival and C-form rows will be removed together. Restore any time from the Recycle Bin."
        label={delTarget ? `${delTarget.customer || "Unknown"} · ${delTarget.programme || ""} · ${delTarget.start_date || ""}` : ""}
        onCancel={() => setDelTarget(null)}
        onConfirm={async () => { await del(delTarget.id); setDelTarget(null); }}
      />
      {pendingUpload && (
        <UploadPreviewModal file={pendingUpload} endpoint="/program-bookings/upload"
          label="program bookings"
          onClose={() => setPendingUpload(null)}
          onDone={load}/>
      )}
    </div>
  );
}
